/**
 * ENG-5750 — pure helpers for the impersonation "introduce yourself"
 * SessionStart hook.
 *
 * When an operator `agt impersonate connect`s, a SessionStart hook fires
 * on the fresh Claude Code boot and shells out to `agt impersonate
 * introduce`. That command reads the persona snapshot already written to
 * the project dir at connect time — `CLAUDE.md` (identity) and
 * `.mcp.json` (connected MCP servers / integrations) — and prints a
 * short first-person introduction. No network, no token use.
 *
 * These three functions are the testable core: parsing identity out of
 * the generated CLAUDE.md, parsing the integration list out of
 * .mcp.json, and rendering the introduction text. The command wrapper
 * (impersonate.ts) handles the manifest guard, file IO, and soft-fail.
 *
 * The CLAUDE.md shape parsed here is produced by `generateClaudeMd`
 * (packages/core/src/provisioning/frameworks/claudecode/identity.ts):
 *
 *     # <display_name>
 *
 *     You are **<display_name>**, **<role>** in the **<team>** team at **<org>**.
 *     ...
 */

export interface ParsedIdentity {
  displayName: string | null;
  role: string | null;
}

/**
 * Extract the agent's display name and role from a generated CLAUDE.md.
 *
 * - `displayName` ← the first `# <heading>` line.
 * - `role` ← the bolded role in the `You are **<name>**, **<role>**…`
 *   identity sentence.
 *
 * Either field is `null` when its source line is absent — callers
 * degrade gracefully rather than fail (ENG-5750 soft-fail AC).
 */
export function parseIdentityFromClaudeMd(content: string): ParsedIdentity {
  const headingMatch = content.match(/^#\s+(.+?)\s*$/m);
  const displayName = headingMatch?.[1]?.trim() || null;

  // `You are **Koda**, **Software Developer** in the **…** team…`
  // The role is the SECOND bolded span on the identity line. Anchored to
  // the `You are ` prefix so we don't accidentally match other bold text.
  const roleMatch = content.match(
    /^You are \*\*[^*]+\*\*,\s*\*\*([^*]+)\*\*/m,
  );
  const role = roleMatch?.[1]?.trim() || null;

  return { displayName, role };
}

/**
 * Extract the connected integration / MCP server names from a generated
 * `.mcp.json` (the `mcpServers` object's keys), prettified for display.
 *
 * Returns `[]` on malformed JSON or a missing/empty `mcpServers` map —
 * the caller treats an empty list as "no integrations" rather than an
 * error.
 */
export function parseIntegrationsFromMcpJson(content: string): string[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return [];
  }
  const servers = (parsed as { mcpServers?: unknown } | null)?.mcpServers;
  if (!servers || typeof servers !== 'object' || Array.isArray(servers)) {
    return [];
  }
  return Object.keys(servers as Record<string, unknown>).map(prettifyServerName);
}

/**
 * Turn an MCP server key (`direct-chat`, `task_channel`) into a
 * human-readable label (`Direct Chat`, `Task Channel`) for the intro.
 */
function prettifyServerName(key: string): string {
  return key
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface IntroductionInput {
  displayName: string | null;
  codeName: string;
  role: string | null;
  integrations: string[];
}

/**
 * Render the first-person introduction printed into the fresh session.
 *
 * Shape (fields degrade independently when missing):
 *
 *     I'm Koda (koda), Software Developer.
 *
 *     Connected integrations: Slack, Telegram, Augmented.
 *
 * - No display name → fall back to the code name as the spoken name.
 * - No role → drop the role clause.
 * - No integrations → say so explicitly rather than emit a dangling line.
 */
export function buildIntroduction(input: IntroductionInput): string {
  const { displayName, codeName, role, integrations } = input;

  const spokenName = displayName || codeName;
  // Show "(code-name)" only when it adds information beyond the spoken name.
  const codeSuffix = displayName && displayName !== codeName ? ` (${codeName})` : '';
  const roleClause = role ? `, ${role}` : '';

  const lines: string[] = [`I'm ${spokenName}${codeSuffix}${roleClause}.`, ''];

  if (integrations.length > 0) {
    lines.push(`Connected integrations: ${integrations.join(', ')}.`);
    // ENG-5812: when the operator asks "list your integrations", call
    // `tools/list` on each MCP server above and report what each
    // actually exposes — do NOT narrate the integration list from
    // CHARTER.md. CHARTER describes the agent's design intent;
    // tools/list is the running ground truth. Real example of why:
    // an agent whose CHARTER mentions Linear via Composio + AWS
    // native that's actually only mounted as `augmented` should
    // answer "augmented exposes the following tools: …" not
    // "I have Linear and AWS".
    lines.push('');
    lines.push(
      "If the operator asks what you can do, call each MCP server's " +
      '`tools/list` and report the actual tool inventory. Do not narrate ' +
      'capabilities from CHARTER.md — it describes intent, not runtime ' +
      'state. Tools that fail to enumerate (server failed to spawn, env ' +
      'missing, etc.) should be reported as such, not silently omitted.',
    );
  } else {
    lines.push('No integrations are connected.');
  }

  return lines.join('\n');
}
